import React from 'react';
import { X, Edit, Trash2 } from 'lucide-react';

const DetailViewModal = ({ isOpen, onClose, title, data, fields, customFields, onEdit, onDelete, canModify, currency }) => {
  if (!isOpen || !data) return null;

  const formatValue = (value, format) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (format === 'currency') return `${currency || 'USD'} ${Number(value).toLocaleString()}`;
    if (format === 'number') return Number(value).toLocaleString();
    if (format === 'percentage') return `${value}%`;
    return value;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Standard Fields */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((field) => (
                <div key={field.key} className={field.fullWidth ? 'md:col-span-2' : ''}>
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {field.label}
                    </label>
                    <div className="text-base font-semibold text-gray-900">
                      {formatValue(data[field.key], field.format)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Fields */}
          {customFields && customFields.length > 0 && data.custom_fields && Object.keys(data.custom_fields).length > 0 && (
            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-700">Custom Fields</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customFields.map((field) => {
                  const value = data.custom_fields[field.name];
                  if (!value && value !== 0 && value !== false) return null;
                  
                  return (
                    <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          {field.name}
                        </label>
                        <div className="text-base text-gray-900">
                          {field.type === 'textarea' ? (
                            <div className="whitespace-pre-wrap">{value}</div>
                          ) : (
                            <span>{value.toString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Custom Fields Message */}
          {customFields && customFields.length > 0 && (!data.custom_fields || Object.keys(data.custom_fields).length === 0) && (
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p className="text-sm text-gray-500 italic">No custom field data available</p>
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        {canModify && (
          <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
            <button
              onClick={() => {
                onEdit(data);
                onClose();
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit size={18} className="mr-2" />
              Edit
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this record?')) {
                  onDelete(data.id);
                  onClose();
                }
              }}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 size={18} className="mr-2" />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailViewModal;
